import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const NAVY = '#1B2A4A';
const GOLD = '#C9A84C';
const EMERALD = '#10B981';
const AMBER = '#F59E0B';
const TEAL = '#14B8A6';

type SalesPoint = { month: string; ventes: number; commandes: number };
type ProductPoint = { name: string; revenus: number };
type StatusPoint = { name: string; value: number; fill: string };

type VendorChartsProps = {
  salesMonthly: SalesPoint[];
  topProducts: ProductPoint[];
  orderStatusDist: StatusPoint[];
  highlights: Array<{ label: string; value: string; sub: string; icon: string; color: string }>;
};

const formatFCFA = (v: number) =>
  v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

const RADIAN = Math.PI / 180;
interface PieLabelProps {
  cx: number; cy: number; midAngle: number; innerRadius: number;
  outerRadius: number; percent: number;
}
const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: PieLabelProps) => {
  if (percent < 0.08) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="bold">{`${(percent * 100).toFixed(0)}%`}</text>;
};

export default function VendorCharts({
  salesMonthly,
  topProducts,
  orderStatusDist,
  highlights,
}: VendorChartsProps) {
  return (
    <div className="space-y-5">
      {/* Row 1 : Sales area + Top products bar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Area chart — Ventes mensuelles */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
              <i className="ri-line-chart-line text-emerald-700 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Évolution des ventes</h3>
              <p className="text-xs text-gray-400">Revenus & commandes sur 7 mois</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={salesMonthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="vendSalesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={EMERALD} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={EMERALD} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="vendCmdGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={formatFCFA} width={38} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  name === 'ventes' ? `${value.toLocaleString('fr-FR')} FCFA` : value,
                  name === 'ventes' ? 'Revenus' : 'Commandes',
                ]}
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="ventes" stroke={EMERALD} strokeWidth={2.5} fill="url(#vendSalesGrad)" name="ventes" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="commandes" stroke={GOLD} strokeWidth={2} fill="url(#vendCmdGrad)" name="commandes" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart — Top produits */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-lg">
              <i className="ri-bar-chart-box-line text-navy text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Top 5 produits</h3>
              <p className="text-xs text-gray-400">Par revenus générés</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={topProducts} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }} barSize={14}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={formatFCFA} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={75} />
              <Tooltip formatter={(v: number) => [`${v.toLocaleString('fr-FR')} FCFA`, 'Revenus']} />
              <Bar dataKey="revenus" radius={[0, 6, 6, 0]} name="Revenus">
                {topProducts.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? NAVY : i === 1 ? GOLD : i === 2 ? EMERALD : i === 3 ? AMBER : TEAL} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2 : Order status pie + summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-navy/10 rounded-lg">
              <i className="ri-pie-chart-2-line text-navy text-xs"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Statut commandes</h3>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={orderStatusDist} cx="50%" cy="50%" outerRadius={68} innerRadius={32}
                dataKey="value" labelLine={false} label={renderLabel}>
                {orderStatusDist.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {orderStatusDist.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.fill }}></span>
                  <span className="text-xs text-gray-600">{s.name}</span>
                </div>
                <span className="text-xs font-bold text-navy">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary cards */}
        <div className="sm:col-span-2 grid grid-cols-2 gap-4">
          {highlights.map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${c.color} flex-shrink-0`}>
                <i className={`${c.icon} text-white text-base`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{c.label}</p>
                <p className="text-base font-bold text-navy">{c.value}</p>
                <p className="text-xs text-emerald-600 font-medium">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
